/**
 * Matches the customer's current Hulk option selection against the
 * product_option_images metafield (JSON in <script id="product-option-images">)
 * and reports the matching combination.
 *
 * Format (use "options", or just "key" with "options" left out):
 *   { "combinations": [ { "id": "...", "options": { "<Option name>": "<value>", ... }, "images": [...] } ] }
 * "options" is optional: without it the combination is matched by its "key"
 * ("value1|value2|..."): every value must be among the currently selected
 * values (any order, any option). Among matches, the one with the most
 * conditions wins.
 *
 * Works with the Hulk Product Options app embed: it observes the options the
 * app renders into [id^="hulkapps_options_"] (.hulkapps_option elements) and
 * never edits them. The result is dispatched as a `hulk:option-images-matched`
 * event, which hulk-option-images-gallery.js uses to swap the product gallery.
 */
(() => {
  const SOURCE_ID = 'product-option-images';
  const ROOT_SELECTOR = '[id^="hulkapps_options_"]';
  // Verbose console logging. Also toggle at runtime: window.hulkOptionImagesDebug = true.
  const DEBUG = false;

  const log = (...args) => {
    if (DEBUG || window.hulkOptionImagesDebug === true) console.log('[hulk-option-images]', ...args);
  };
  const warn = (...args) => console.warn('[hulk-option-images]', ...args);

  // Case-insensitive, trimmed, repeated spaces collapsed: "quick  SHIP" == "Quick Ship".
  const normalize = (text) => String(text ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

  // "Medium - Power" -> "Power": the part after the last " - " is the display form.
  const displayForm = (text) => {
    const value = String(text ?? '');
    const index = value.lastIndexOf(' - ');
    return index > -1 ? value.slice(index + 3) : value;
  };

  function loadCombinations() {
    const script = document.getElementById(SOURCE_ID);
    if (!script) {
      warn(`<script id="${SOURCE_ID}"> not found on the page`);
      return [];
    }

    const raw = script.textContent.trim();
    if (!raw || raw === 'null') {
      warn('metafield product_option_images is empty (null) for this product');
      return [];
    }

    try {
      const data = JSON.parse(raw);
      if (!Array.isArray(data?.combinations)) {
        warn('JSON parsed but has no "combinations" array. Top-level keys:', Object.keys(data || {}));
        return [];
      }
      return data.combinations;
    } catch (error) {
      warn('JSON.parse failed:', error.message);
      return [];
    }
  }

  class HulkOptionImages {
    constructor(root, combinations) {
      this.root = root;
      this.combinations = combinations;
      this.lastKey = null;
      this.pendingCheck = null;
    }

    start() {
      // Hulk updates the selected state asynchronously (class changes) and may
      // re-render options, so watch the DOM as well as change/click.
      this.root.addEventListener('change', () => this.scheduleCheck());
      this.root.addEventListener('click', () => this.scheduleCheck());
      new MutationObserver(() => this.scheduleCheck()).observe(this.root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style']
      });
      this.scheduleCheck();
    }

    scheduleCheck() {
      clearTimeout(this.pendingCheck);
      this.pendingCheck = setTimeout(() => this.check(), 50);
    }

    // Option name as shown by Hulk: aria-label of the name element, else its own text
    // (the visible text can carry a ": Selected value" suffix added by the theme).
    getOptionName(optionEl) {
      const nameEl = optionEl.querySelector(':scope > .hulkapps_option_name');
      if (!nameEl) return '';
      const label = nameEl.getAttribute('aria-label');
      if (label) return label.trim();
      const firstText = [...nameEl.childNodes].find((node) => node.nodeType === 3 && node.textContent.trim());
      return (firstText ? firstText.textContent : nameEl.textContent).split(':')[0].trim();
    }

    // Options turned off by a condition rule are hidden with [hidden] or display:none
    // on the option or one of its wrappers.
    isHidden(el) {
      for (let node = el; node && node !== this.root; node = node.parentElement) {
        if (node.hidden || node.style.display === 'none') return true;
      }
      return false;
    }

    // Selected value texts of one option, for every Hulk option type.
    getSelectedValues(optionEl) {
      const values = [];
      const add = (value) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') values.push(String(value).trim());
      };

      // Button options: the selected button's label and its radio value.
      optionEl.querySelectorAll('.hulkapps_option_child.button_selected').forEach((child) => {
        add(child.querySelector(':scope > span')?.innerText);
        add(child.querySelector('input')?.value);
        add(child.getAttribute('aria-label'));
      });

      // Dropdowns: selected option (skip the placeholder = first, empty-looking option).
      optionEl.querySelectorAll('select').forEach((select) => {
        const selected = select.options[select.selectedIndex];
        if (!selected || select.selectedIndex === 0) return;
        add(selected.value);
        add(selected.textContent);
      });

      // Radios, checkboxes and swatches.
      optionEl.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked').forEach((input) => {
        add(input.value);
        const label = input.closest('label') || (input.id && optionEl.querySelector(`label[for="${CSS.escape(input.id)}"]`));
        if (label) add(label.innerText);
        // Swatch tiles: the value text sits next to the input, not in a <label>.
        const tile = input.closest('.hulkapps_swatch_option, .hulkapps_option_child');
        if (tile && tile !== label) add(tile.innerText);
        add(input.getAttribute('aria-label'));
      });

      return values;
    }

    // Map of normalized option name -> Set of normalized selected values.
    // Option names and values are registered in raw and display form
    // ("Medium - Power" and "Power"). Hidden options are skipped.
    getSelection() {
      const selection = new Map();
      selection.allValues = new Set();

      this.root.querySelectorAll('.hulkapps_option').forEach((optionEl) => {
        if (this.isHidden(optionEl)) return;

        const name = this.getOptionName(optionEl);
        const values = this.getSelectedValues(optionEl);
        if (!name || !values.length) return;

        [name, displayForm(name)].forEach((optionName) => {
          const nameKey = normalize(optionName);
          if (!selection.has(nameKey)) selection.set(nameKey, new Set());
          values.forEach((value) => {
            selection.get(nameKey).add(normalize(value));
            selection.get(nameKey).add(normalize(displayForm(value)));
          });
        });
        values.forEach((value) => {
          selection.allValues.add(normalize(value));
          selection.allValues.add(normalize(displayForm(value)));
        });
      });

      return selection;
    }

    // A name of null means "any option": the value just has to be one of the
    // currently selected values (used for key-only combinations).
    has(selection, name, value) {
      if (name === null) return selection.allValues.has(normalize(value));
      return Boolean(selection.get(normalize(name))?.has(normalize(value)));
    }

    // What a combination requires. With "options" ({ "Option name": "value" })
    // each value must be selected under that option. Without it, the "key"
    // ("value1|value2|...") is used: every value must be among the current
    // selected values, in any order and under any option.
    getConditions(combination) {
      if (combination.options && Object.keys(combination.options).length) {
        return Object.entries(combination.options).map(([name, value]) => ({ name, value }));
      }
      return String(combination.key || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => ({ name: null, value }));
    }

    // Among all matching combinations the most specific one (most conditions) wins.
    findMatch(selection) {
      let best = null;
      let bestCount = 0;

      this.combinations.forEach((combination) => {
        const conditions = this.getConditions(combination);
        if (!conditions.length) return;
        if (!conditions.every(({ name, value }) => this.has(selection, name, value))) return;
        if (conditions.length > bestCount) {
          best = combination;
          bestCount = conditions.length;
        }
      });

      return best;
    }

    check() {
      const selection = this.getSelection();
      const match = this.findMatch(selection);
      const key = match ? match.id : 'none';

      if (DEBUG || window.hulkOptionImagesDebug === true) this.logMismatches(selection);
      log('selection:', Object.fromEntries([...selection].map(([name, values]) => [name, [...values]])), '->', key);
      if (key === this.lastKey) return;
      this.lastKey = key;

      this.root.dispatchEvent(
        new CustomEvent('hulk:option-images-matched', { bubbles: true, detail: { combination: match } })
      );
    }
  }

  HulkOptionImages.prototype.logMismatches = function (selection) {
    const rows = this.combinations.map((combination) => ({
      id: combination.id,
      missing: this.getConditions(combination)
        .filter(({ name, value }) => !this.has(selection, name, value))
        .map(({ value }) => value)
        .join(' | ') || 'MATCH'
    }));
    console.table(rows.sort((a, b) => a.missing.length - b.missing.length).slice(0, 8));
  };

  function init() {
    const combinations = loadCombinations();
    if (!combinations.length) return;

    let started = false;
    const start = () => {
      const root = document.querySelector(ROOT_SELECTOR);
      if (started || !root) return Boolean(root);
      started = true;
      const instance = new HulkOptionImages(root, combinations);
      instance.start();
      // Console helper: window.hulkOptionImages.check() re-runs the check manually.
      window.hulkOptionImages = instance;
      return true;
    };

    if (start()) return;

    // The Hulk app renders its options after page load: wait for the container.
    const observer = new MutationObserver(() => {
      if (start()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
